import type { SxProps, Theme } from "@mui/material/styles";
import { dashboardInteractiveFocusSx } from "../Dashboard/dashboard.styles";

/**
 * Dashboard2 type ramp — fixed rem scale (~1.125 ratio), Fira Sans via MUI theme.
 * Roles: pageTitle → sectionTitle → metadata → sectionLabel → data.
 */
const dashboard2TypePageTitle = {
  fontSize: "1.125rem",
  fontWeight: 600,
  lineHeight: 1.35,
  letterSpacing: "-0.01em",
  fontKerning: "normal"
} as const;

const dashboard2TypeSectionTitle = {
  fontSize: "1rem",
  fontWeight: 600,
  lineHeight: 1.3,
  fontKerning: "normal"
} as const;

const dashboard2TypeSectionLabel = {
  fontSize: "0.75rem",
  fontWeight: 600,
  lineHeight: 1.25,
  letterSpacing: "0.02em",
  fontKerning: "normal"
} as const;

const dashboard2TypeMetadata = {
  fontSize: "0.875rem",
  fontWeight: 400,
  lineHeight: 1.45,
  fontKerning: "normal"
} as const;

const dashboard2TypeMetadataStrong = {
  fontSize: "0.875rem",
  fontWeight: 600,
  lineHeight: 1.35,
  fontKerning: "normal"
} as const;

const dashboard2TypeData = {
  fontSize: "0.75rem",
  fontWeight: 600,
  lineHeight: 1.3,
  fontVariantNumeric: "tabular-nums",
  fontKerning: "normal"
} as const;

/** Welcome banner (`h1`) — one line of context, max comfortable measure. */
export const dashboard2PageIntroTitleSx: SxProps<Theme> = {
  ...dashboard2TypePageTitle,
  textWrap: "balance",
  overflowWrap: "anywhere",
  minWidth: 0,
  maxWidth: "65ch"
};

/** Section headings (`h2` / `h3`). */
export const dashboard2SectionIntroTitleSx: SxProps<Theme> = {
  ...dashboard2TypeSectionTitle,
  textWrap: "balance",
  overflowWrap: "anywhere",
  minWidth: 0
};

/** Week plan block heading — one step above section title. */
export const dashboard2WeekPlanTitleSx: SxProps<Theme> = {
  ...dashboard2TypePageTitle,
  textWrap: "balance",
  overflowWrap: "anywhere",
  minWidth: 0
};

/** Fieldset legends and tier labels. */
export const dashboard2SectionLabelBaseSx: SxProps<Theme> = {
  ...dashboard2TypeSectionLabel,
  px: 0.75,
  width: "auto"
};

/** Icon + title + subtitle section header (above the bordered panel). */
export const dashboard2SectionFieldsetLegendSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  gap: 0.75,
  width: "100%",
  minWidth: 0,
  px: 0
};

export const dashboard2SectionFieldsetLegendTitleSx: SxProps<Theme> = {
  ...dashboard2TypeSectionTitle,
  fontSize: "0.875rem",
  lineHeight: 1.3,
  color: "text.primary"
};

export const dashboard2SectionFieldsetLegendDescriptionSx: SxProps<Theme> = {
  ...dashboard2TypeMetadata,
  fontSize: "0.75rem",
  lineHeight: 1.35,
  color: "text.secondary"
};

/** Secondary prose under a heading. */
export const dashboard2SectionDescriptionSx: SxProps<Theme> = {
  ...dashboard2TypeMetadata,
  textWrap: "pretty",
  minWidth: 0,
  overflowWrap: "anywhere",
  color: "text.secondary"
};

/** Standalone metadata line (progress, hints). */
export const dashboard2MetadataTextSx: SxProps<Theme> = {
  ...dashboard2TypeMetadata,
  textWrap: "pretty",
  minWidth: 0,
  overflowWrap: "anywhere",
  color: "text.secondary"
};

/** Week column day labels (Mon 16 …). */
export const dashboard2DayHeaderTextSx = (theme: Theme, isToday = false): SxProps<Theme> => ({
  ...dashboard2TypeData,
  textAlign: "center",
  pb: 0.5,
  borderBottom: 1,
  borderColor: isToday ? "primary.main" : "divider",
  color: isToday
    ? theme.palette.mode === "dark"
      ? theme.palette.primary.light
      : theme.palette.primary.main
    : "text.primary"
});

/** Week navigation label button — layout/typography only; uses MUI contained surface. */
export const dashboard2WeekNavLabelButtonSx: SxProps<Theme> = {
  ...dashboardInteractiveFocusSx,
  ...dashboard2TypeMetadataStrong,
  fontVariantNumeric: "tabular-nums",
  minWidth: 44,
  minHeight: 44,
  px: 1.5,
  py: 0.5,
  letterSpacing: 0,
  textTransform: "none"
};

/** Week picker dialog center label. */
export const dashboard2WeekPickerCenterLabelSx: SxProps<Theme> = {
  ...dashboard2TypeSectionTitle,
  flex: 1,
  textAlign: "center",
  minWidth: 0,
  px: 0.5,
  fontVariantNumeric: "tabular-nums"
};

/** Compact count badges on status surfaces. */
export const dashboard2AdditionalBookingBadgeTextSx: SxProps<Theme> = {
  ...dashboard2TypeSectionLabel,
  lineHeight: 1.4
};

/** Inline tertiary links (manage colleagues). */
export const dashboard2TertiaryLinkTextSx: SxProps<Theme> = {
  ...dashboard2TypeMetadataStrong
};
